import i18n from "../../i18n";
import {
    AppointmentsReport,
    Granularity,
    RevenueReport,
    ServiceRevenue,
    TeamMemberRevenue,
} from '../../model/report/Report';

const API_URL = process.env.REACT_APP_REPORT_SERVICE_BASE_URL || '';

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

/** Date inputs are YYYY-MM-DD; downstream wants ISO LOCAL datetime. */
const startIso = (d: string) => `${d}T00:00:00`;
const endIso = (d: string) => `${d}T23:59:59`;

export async function getRevenueReport(
    businessId: number | string,
    start: string,
    end: string,
    granularity: Granularity,
    token: string
): Promise<RevenueReport> {
    const data = await gqlPost<{ revenueReport: RevenueReport }>(
        token,
        `query($businessId: ID!, $start: String!, $end: String!, $g: Granularity) {
            revenueReport(businessId: $businessId, start: $start, end: $end, granularity: $g) {
                gross net refunded discountTotal count averageTicket
                byMethod { method gross count }
                series { period gross net count }
            }
        }`,
        { businessId: String(businessId), start: startIso(start), end: endIso(end), g: granularity }
    );
    return data.revenueReport;
}

export async function getRevenueByTeamMember(
    businessId: number | string,
    start: string,
    end: string,
    token: string
): Promise<TeamMemberRevenue[]> {
    const data = await gqlPost<{ revenueByTeamMember: TeamMemberRevenue[] }>(
        token,
        `query($businessId: ID!, $start: String!, $end: String!) {
            revenueByTeamMember(businessId: $businessId, start: $start, end: $end) {
                teamMemberId teamMemberName appointments transactions gross net refunded
            }
        }`,
        { businessId: String(businessId), start: startIso(start), end: endIso(end) }
    );
    return data.revenueByTeamMember;
}

export async function getRevenueByService(
    businessId: number | string,
    start: string,
    end: string,
    token: string
): Promise<ServiceRevenue[]> {
    const data = await gqlPost<{ revenueByService: ServiceRevenue[] }>(
        token,
        `query($businessId: ID!, $start: String!, $end: String!) {
            revenueByService(businessId: $businessId, start: $start, end: $end) {
                serviceId serviceName categoryName count gross net averageTicket
            }
        }`,
        { businessId: String(businessId), start: startIso(start), end: endIso(end) }
    );
    return data.revenueByService;
}

export async function getAppointmentsReport(
    businessId: number | string,
    start: string,
    end: string,
    granularity: Granularity,
    token: string
): Promise<AppointmentsReport> {
    const data = await gqlPost<{ appointmentsReport: AppointmentsReport }>(
        token,
        `query($businessId: ID!, $start: String!, $end: String!, $g: Granularity) {
            appointmentsReport(businessId: $businessId, start: $start, end: $end, granularity: $g) {
                total cancellationRate noShowRate
                byStatus { status count }
                byTeamMember { teamMemberId teamMemberName total completed cancelled noShow }
                series { period count }
            }
        }`,
        { businessId: String(businessId), start: startIso(start), end: endIso(end), g: granularity }
    );
    return data.appointmentsReport;
}
